// GridExcelExportMenuItemCompat.tsx
import * as React from "react";

import { Translate } from "@/app/application/Translate";
import TableChartIcon from "@mui/icons-material/TableChart";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import {
	type GridApi,
	type GridColDef,
	type GridRowId,
	gridFilteredSortedRowIdsSelector,
	gridVisibleColumnFieldsSelector,
	useGridApiContext,
} from "@mui/x-data-grid";
import ExcelJS from "exceljs";

export type ExcelOptions = { fileName?: string; sheetName?: string };
type ExportRow = Record<string, unknown>;

const headerText = (col: GridColDef) => {
	if (typeof col.headerName === "string" || typeof col.headerName === "number")
		return String(col.headerName);
	return col.field;
};

const asText = (value: unknown): string => {
	if (value == null) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean")
		return String(value);
	if (Array.isArray(value)) return value.map(asText).join(", ");
	if (value && typeof value === "object") {
		const maybeName = (value as { Name?: unknown }).Name;
		if (typeof maybeName === "string" || typeof maybeName === "number")
			return String(maybeName);
		try {
			return JSON.stringify(value);
		} catch {
			return "";
		}
	}
	if (typeof value === "bigint") return value.toString();
	if (typeof value === "symbol") return value.description ?? "symbol";
	if (typeof value === "function") return "function";
	return "";
};

const serializeGridCell = (
	api: GridApi,
	id: GridRowId,
	field: string,
): string => {
	const params = api.getCellParams(id, field);
	return asText(params.formattedValue ?? params.value);
};

/** Serialize a cell value without grid API (for standalone export from nodeList). */
function serializeCellFromRow(col: GridColDef, row: ExportRow): string {
	return asText(row[col.field]);
}

function downloadWorkbook(buffer: ArrayBuffer, fileName: string) {
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
	a.click();
	URL.revokeObjectURL(url);
}

/** Export nodeList to Excel without grid API (e.g. for mobile card view). */
export async function exportArchiveDataToExcel(
	nodeList: ExportRow[],
	columns: GridColDef[],
	_timezone: string,
	options: ExcelOptions = {},
) {
	const fileName = options.fileName || "Export";
	const sheetName = options.sheetName || "Sheet1";
	const exportCols = columns.filter((c) => !c.disableExport);
	const header = exportCols.map((c) => headerText(c));
	const body = nodeList.map((row) =>
		exportCols.map((c) => serializeCellFromRow(c, row)),
	);

	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(sheetName);
	worksheet.addRow(header);
	body.forEach((row) => worksheet.addRow(row));
	header.forEach((h, i) => {
		const maxLen = Math.max(
			String(h).length,
			...body.map((r) => String(r[i] ?? "").length),
		);
		worksheet.getColumn(i + 1).width = Math.min(Math.max(10, maxLen + 2), 60);
	});

	const buffer = await workbook.xlsx.writeBuffer();
	downloadWorkbook(buffer as ArrayBuffer, fileName);
}

/** Call this to export the grid to Excel (e.g. from a button). */
export async function exportArchiveGridToExcel(
	apiRef: { current: GridApi | null },
	options: ExcelOptions = {},
) {
	const api = apiRef?.current;
	if (!api) return;
	const fileName = options.fileName || "Export";
	const sheetName = options.sheetName || "Sheet1";

	const visibleFields = gridVisibleColumnFieldsSelector(apiRef);
	const columns = api
		.getAllColumns()
		.filter((c) => visibleFields.includes(c.field) && !c.disableExport);
	const rowIds = gridFilteredSortedRowIdsSelector(apiRef);

	const header = columns.map((c) => headerText(c));
	const body = rowIds.map((id) => {
		return columns.map((c) => serializeGridCell(api, id, c.field));
	});

	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(sheetName);
	worksheet.addRow(header);
	body.forEach((row) => worksheet.addRow(row));
	header.forEach((h, i) => {
		const maxLen = Math.max(
			String(h).length,
			...body.map((r) => asText(r[i]).length),
		);
		worksheet.getColumn(i + 1).width = Math.min(Math.max(10, maxLen + 2), 60);
	});

	const buffer = await workbook.xlsx.writeBuffer();
	downloadWorkbook(buffer as ArrayBuffer, fileName);
}

type Props = {
	options?: ExcelOptions;
	icon?: React.ReactNode;
	label?: React.ReactNode;
};

export function GridExcelExportMenuItemCompat({
	options,
	icon = <TableChartIcon fontSize="small" />,
	label,
}: Props) {
	const apiRef = useGridApiContext();
	const handleExport = () => {
		void exportArchiveGridToExcel(apiRef, options);
	};

	return (
		<MenuItem onClick={handleExport}>
			<ListItemIcon>{icon}</ListItemIcon>
			<ListItemText>
				{label ?? (
					<Translate String="DataGrid.Export.Excel">Export to Excel</Translate>
				)}
			</ListItemText>
		</MenuItem>
	);
}
